import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { jwtDecode } from "jwt-decode";
import { isAfter, secondsToMilliseconds, toDate } from "date-fns";
import {
  hostsCompliance,
  hostsInstitutional,
  hostsWebSocket,
} from "../variables/global";
import { deeplyTrim, removeUndefinedValues } from "utils";
import versionData from "../version.json";

export const getPath = (apiId, useCoreV1Domain = false) => {
  let baseHosts;

  switch (apiId) {
    case "institutional":
      baseHosts = hostsInstitutional;
      break;
    case "compliance":
      baseHosts = hostsCompliance;
      break;
    case "websocket":
      baseHosts = hostsWebSocket;
      break;
    default:
      return;
  }

  const savedEnv = localStorage.getItem("app_env");
  const env =
    savedEnv && baseHosts[savedEnv] ? savedEnv : process.env.REACT_APP_ENV;

  return baseHosts[env];
};

export const getBaseQuery = (apiId) => {
  const baseUrl = getPath(apiId);
  return fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState, endpoint }) => {
      const token = getState()?.auth?.token;
      if (token) {
        headers.set("authorization", token);
      }
      headers.set("Content-Type", "application/json");
      if (versionData && versionData.version) {
        headers.set("X-App-Version", versionData.version);
      }

      return headers;
    },
  });
};

export const isTokenValidFn = (token) => {
  if (token) {
    const { exp: tokenExpirationDate } = jwtDecode(token);
    const formattedExpiration = toDate(
      secondsToMilliseconds(tokenExpirationDate),
    );
    const now = toDate(new Date());
    return isAfter(formattedExpiration, now);
  }
  // if there's no token assume the endpoint is not protected and don't check
  return true;
};

export const getAuthWebSocketQuery = (apiId, url, state) => {
  const token = state?.auth?.token;
  const isValidToken = isTokenValidFn(token);

  const baseUrl = getPath(apiId);

  if (!isValidToken) {
    window.location.replace("/login?tokenExpired=true");
    return; // Early return to prevent further execution after redirecting
  }

  const query = `${baseUrl}/ws${url}`;

  return query;
};

export const customFetchBase = (apiId) => async (args, api, extraOptions) => {
  const token = args.headers?.authorization;
  const isValidToken = isTokenValidFn(token);

  if (!isValidToken) {
    window.location.replace("/login?tokenExpired=true");
  }

  const baseQuery = getBaseQuery(apiId);

  if (typeof args === "object") {
    // Clean request body

    if ("body" in args && args.body) {
      if (Array.isArray(args.body)) {
        args = {
          ...args,
          body: args.body.map((o) => removeUndefinedValues(deeplyTrim(o))),
        };
      } else {
        args = { ...args, body: removeUndefinedValues(deeplyTrim(args.body)) };
      }
    }

    // Clean query parameters
    if ("params" in args && args.params) {
      args = {
        ...args,
        params: removeUndefinedValues(deeplyTrim(args.params)),
      };
    }
  }
  const result = await baseQuery(args, api, {});

  if (result?.data) {
    return { data: result.data };
  }
  if (result.data === null) {
    return {};
  }
  if (result?.error) {
    if (result?.error.status === 401) {
      window.location.replace("/login?tokenExpired=true");
    }
    return { error: result.error };
  }
  return { error: "Something went wrong" };
};

// The compliance engine speaks x-api-key, not Firebase JWTs, and a 401 from it
// means a missing/wrong key — never an expired session — so this base neither
// validates the auth token nor redirects to /login. Dev-sandbox caveat: the
// key is read from REACT_APP_COMPLIANCE_API_KEY at build time and therefore
// ships in the served bundle; acceptable only while the engine fronts the CRM
// directly. The production path is the institutional backend owning /leads.
export const complianceFetchBase = () => async (args, api) => {
  const baseQuery = fetchBaseQuery({
    baseUrl: getPath("compliance"),
    prepareHeaders: (headers, { getState }) => {
      const key = process.env.REACT_APP_COMPLIANCE_API_KEY;
      if (key) {
        headers.set("x-api-key", key);
      }
      const token = getState()?.auth?.token;
      if (token) {
        headers.set("authorization", token);
      }
      // Default only — document uploads set the file's own type per request.
      if (!headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }
      return headers;
    },
  });

  if (typeof args === "object") {
    // Clean JSON payloads only — a File/Blob body (document upload) must
    // pass through untouched.
    const isBinary = typeof Blob !== "undefined" && args.body instanceof Blob;
    if ("body" in args && args.body && !isBinary) {
      args = { ...args, body: removeUndefinedValues(deeplyTrim(args.body)) };
    }
    if ("params" in args && args.params) {
      args = {
        ...args,
        params: removeUndefinedValues(deeplyTrim(args.params)),
      };
    }
  }

  const result = await baseQuery(args, api, {});
  if (result?.data) {
    return { data: result.data };
  }
  if (result?.error) {
    return { error: result.error };
  }
  return { error: "Something went wrong" };
};
